import { useState, useEffect } from 'react';
import { Modal, Form, Spinner } from 'react-bootstrap';
import LoadingOverlay from 'react-loading-overlay';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, useFieldArray } from "react-hook-form";
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import moment from 'moment';

// Custom Component
import TournamentForm from '../Form/TournamentForm';
import { PrimaryButton } from '../Button';
import SuccessAlert from '../Alert/SuccessAlert';
import ErrorAlert from '../Alert/ErrorAlert';

// Images
import { Image } from '../../assets/images/svg';

// Utils
import { isMixedEvent, isAgeValueRequired, getGenderCount, getPlayerCount, convertEventType } from '../../utils/helper';

// API Service
import { updateTournamentService, playersCountTournamentService, deleteTournamentService } from '../../service/tournament.service';
import { allSubOrganizerService } from '../../service/subOrganizer.service';

// Redux-Functions
import { selectUser } from "../../redux/userSlice";

const EditTournamentModal = (props) => {
    let { tournamentId } = useParams();
    const navigate = useNavigate();
    const user = useSelector(selectUser);

    const [tournamentImage, setTournamentImage] = useState(null);
    const [subOrganizerList, setSubOrganizerList] = useState([])
    const [loading, setLoading] = useState(false);
    LoadingOverlay.propTypes = undefined

    const { register, control, watch, setValue, reset, handleSubmit, formState: { errors } } = useForm({
        mode: "all"
    })

    const { fields: individualEventsFields, append: individualEventsAppend, remove: individualEventsRemove } = useFieldArray({
        control,
        name: "individual_events"
    });

    const { fields: teamEventsFields, append: teamEventsAppend } = useFieldArray({
        control,
        name: "team_events"
    });

    /**
        * @function fetchSubOrganizerList
        * @params 
        * @description fetch the list of sub organizer
    */
    const fetchSubOrganizerList = async () => {
        try {
            const result = await allSubOrganizerService({
                paginate: 0,
                page: '',
                perPage: '',
                keyword: '',
                status: 1
            });
            if (result?.data?.status) {
                setSubOrganizerList(result?.data?.data?.organizations)
            } else {
                ErrorAlert(result?.response?.data?.message)
            }
        } catch (error) {
            ErrorAlert(error)
        }
    }

    useEffect(() => {
        if (user?.type === 'organizer') {
            fetchSubOrganizerList()
        }
        // eslint-disable-next-line
    }, [user])

    // for setting default values
    useEffect(() => {
        if (props?.tournament && subOrganizerList) {
            let defaultValues = {};
            defaultValues.organization_id = props?.tournament?.subOrganizers?.map(item => {
                return {
                    value: item?.id,
                    label: item?.name
                }
            })
            defaultValues.name = props?.tournament?.name
            defaultValues.description = props?.tournament?.description
            defaultValues.prizes = props?.tournament?.prizes
            defaultValues.terms_and_condition = props?.tournament?.terms_and_condition
            defaultValues.sports_type = props?.tournament?.sports_type
            // if (props?.tournament?.event_details?.filter(item => !item?.team_event_id)?.length > 0 && props?.tournament?.team_event_details?.length > 0) {
            //     defaultValues.category_type = ["INDIVIDUAL", "TEAM"]
            // } else if (props?.tournament?.event_details?.filter(item => !item?.team_event_id)?.length > 0) {
            //     defaultValues.category_type = ["INDIVIDUAL"]
            // } else if (props?.tournament?.team_event_details?.length > 0) {
            //     defaultValues.category_type = ["TEAM"]
            // }
            defaultValues.category_type = ["INDIVIDUAL"]
            defaultValues.registration_start_date = new Date(props?.tournament?.registration_start_date)
            defaultValues.registration_end_date = new Date(props?.tournament?.registration_end_date)
            defaultValues.tournament_start_date = new Date(props?.tournament?.tournament_start_date)
            defaultValues.tournament_end_date = new Date(props?.tournament?.tournament_end_date)
            defaultValues.tournament_start_time = moment(props?.tournament?.tournament_start_time, "hh:mm A").format("HH:mm")
            defaultValues.tournament_end_time = moment(props?.tournament?.tournament_end_time, "hh:mm A").format("HH:mm")
            const individual_events = []
            const team_events = []
            const uniqueIndividualEventType = [...new Set(props?.tournament?.event_details?.filter(item => !item?.team_event_id)?.map(item => item?.event_type))]
            if (uniqueIndividualEventType?.length > 0) {
                uniqueIndividualEventType?.forEach((item) => {
                    individual_events.push({
                        event_type: item === "JUNIOR_SINGLE" ? "GROUP_CAPTAIN" : item,
                        age_groups: props?.tournament?.event_details?.filter(eventItem => eventItem?.event_type === item && !eventItem?.team_event_id)?.map(eventItem => {
                            return {
                                age_group: eventItem?.age_group,
                                age_value: eventItem?.age_value,
                                price: eventItem?.price,
                                number_of_males: eventItem?.number_of_males > 0 ? eventItem?.number_of_males : "",
                                number_of_females: eventItem?.number_of_females > 0 ? eventItem?.number_of_females : "",
                                allowed_registrations: eventItem?.allowed_registrations > 0 ? eventItem?.allowed_registrations : "",
                                is_disabled: !eventItem?.is_disabled,
                                allow_proffesional_players: eventItem?.allow_proffesional_players
                            }
                        })
                    })
                })
            }
            // if (props?.tournament?.team_event_details?.length > 0) {
            //     defaultValues.number_of_team_event = props?.tournament?.team_event_details?.length
            //     props?.tournament?.team_event_details?.forEach((item) => {
            //         const uniqueTeamEventType = [...new Set(props?.tournament?.event_details?.filter(eventItem => eventItem?.team_event_id === item?.id)?.map(item => item?.event_type))]
            //         team_events.push({
            //             team_event_id: item?.id,
            //             event_name: item?.event_name,
            //             number_of_participants: item?.accepted_players_count,
            //             price: item?.price,
            //             event_details: uniqueTeamEventType?.map((uniqueEventItem) => {
            //                 return {
            //                     event_type: uniqueEventItem === "JUNIOR_SINGLE" ? "GROUP_CAPTAIN" : uniqueEventItem,
            //                     age_groups: props?.tournament?.event_details?.filter(eventItem => eventItem?.event_type === uniqueEventItem && eventItem?.team_event_id === item?.id)?.map(eventItem => {
            //                         return {
            //                             age_group: eventItem?.age_group,
            //                             age_value: eventItem?.age_value,
            //                             number_of_males: eventItem?.number_of_males > 0 ? eventItem?.number_of_males : "",
            //                             number_of_females: eventItem?.number_of_females > 0 ? eventItem?.number_of_females : "",
            //                             is_disabled: !eventItem?.is_disabled,
            //                             allow_proffesional_players: eventItem?.allow_proffesional_players
            //                         }
            //                     })
            //                 }
            //             })
            //         })
            //     })
            // }
            defaultValues.individual_events = individual_events
            defaultValues.team_events = team_events
            defaultValues.contact = props?.tournament?.contact
            defaultValues.phone_number = props?.tournament?.phone_number
            defaultValues.address = props?.tournament?.address

            setTournamentImage(props?.tournament?.banner_image)
            reset({ ...defaultValues })
        } else {
            reset()
        }
        // eslint-disable-next-line
    }, [props?.tournament, props.show, subOrganizerList])

    /**
        * @function playersCountTournament
        * @params
        * @description used to count players of tournament
    */
    const playersCountTournament = async () => {
        try {
            setLoading(true)
            const result = await playersCountTournamentService(tournamentId);
            if (result?.data?.status) {
                if (result?.data?.data?.players_count > 0) {
                    ErrorAlert(`This tournament can't be deleted because ${result?.data?.data?.players_count} players are already registered.`)
                } else {
                    deleteTournament()
                }
            } else {
                ErrorAlert(result?.response?.data?.message)
            }
            setLoading(false)
        } catch (error) {
            setLoading(false)
            ErrorAlert(error)
        }
    }

    /**
        * @function deleteTournament
        * @params
        * @description used to delete tournament
    */
    const deleteTournament = async () => {
        try {
            setLoading(true)
            const result = await deleteTournamentService(tournamentId);
            if (result?.data?.status) {
                SuccessAlert(result?.data?.message)
                handleClose()
                navigate('/tournament')
            } else {
                ErrorAlert(result?.response?.data?.message)
            }
            setLoading(false)
        } catch (error) {
            setLoading(false)
            ErrorAlert(error)
        }
    }

    /**
        * @function onSubmit
        * @params formdata
        * @description used to edit tournament
    */
    const onSubmit = async (formdata) => {
        try {
            const data = new FormData()
            data.append('_method', 'PUT')
            data.append('tournament_id', tournamentId)
            if (formdata?.organization_id?.length > 0) {
                formdata?.organization_id?.forEach((item, index) => {
                    data.append(`organization_ids[${index}]`, item?.value)
                })
            }
            data.append('name', formdata?.name)
            data.append('description', formdata?.description)
            data.append('prizes', formdata?.prizes)
            data.append('terms_and_condition', formdata?.terms_and_condition)
            if (tournamentImage instanceof File) {
                data.append('banner_image', tournamentImage)
            }
            data.append('sports_type', formdata?.sports_type)
            data.append('category_type', "INDIVIDUAL")
            data.append('registration_start_date', moment(formdata?.registration_start_date).format("YYYY-MM-DD"))
            data.append('registration_end_date', moment(formdata?.registration_end_date).format("YYYY-MM-DD"))
            data.append('tournament_start_date', moment(formdata?.tournament_start_date).format("YYYY-MM-DD"))
            data.append('tournament_end_date', moment(formdata?.tournament_end_date).format("YYYY-MM-DD"))
            data.append('tournament_start_time', moment(formdata?.tournament_start_time, "hh:mm A").format("HH:mm"))
            data.append('tournament_end_time', moment(formdata?.tournament_end_time, "hh:mm A").format("HH:mm"))
            if (formdata?.category_type?.includes("INDIVIDUAL") && formdata?.individual_events?.length > 0) {
                formdata?.individual_events?.forEach((item, index) => {
                    if (item?.event_type) {
                        data.append(`individual_events[${index}][event_type]`, item?.event_type)
                    }
                    if (item?.age_groups?.length > 0) {
                        item?.age_groups?.forEach((ageGroupsItem, ageGroupsIndex) => {
                            if (ageGroupsItem?.age_group) {
                                data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][age_group]`, ageGroupsItem?.age_group)
                            }
                            if (isAgeValueRequired(ageGroupsItem?.age_group) && ageGroupsItem?.age_value) {
                                data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][age_value]`, ageGroupsItem?.age_value)
                            }
                            if (ageGroupsItem?.price) {
                                data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][price]`, ageGroupsItem?.price)
                            }
                            const genderCount = getGenderCount(item?.event_type);
                            if (genderCount) {
                                data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][number_of_males]`, genderCount?.number_of_males)
                                data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][number_of_females]`, genderCount?.number_of_females)
                            }
                            if (ageGroupsItem?.number_of_males && ageGroupsItem?.number_of_females && isMixedEvent(item?.event_type) && (parseInt(ageGroupsItem?.number_of_males) + parseInt(ageGroupsItem?.number_of_females)) !== getPlayerCount(item?.event_type)) {
                                ErrorAlert(`You need to choose ${getPlayerCount(item?.event_type)} person for ${convertEventType(item?.event_type)} event`)
                                return
                            }
                            if (ageGroupsItem?.number_of_males && isMixedEvent(item?.event_type)) {
                                data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][number_of_males]`, ageGroupsItem?.number_of_males)
                            }
                            if (ageGroupsItem?.number_of_females && isMixedEvent(item?.event_type)) {
                                data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][number_of_females]`, ageGroupsItem?.number_of_females)
                            }
                            data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][allowed_registrations]`, ageGroupsItem?.allowed_registrations)
                            data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][is_disabled]`, ageGroupsItem?.is_disabled ? 0 : 1)
                            data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][allow_proffesional_players]`, ageGroupsItem?.allow_proffesional_players ? 1 : 0)
                        })
                    }
                })
            }
            if (formdata?.category_type?.includes("TEAM") && formdata?.team_events?.length > 0) {
                formdata?.team_events?.forEach((item, index) => {
                    data.append(`team_events[${index}][team_event_id]`, item?.team_event_id || "")
                    if (item?.event_name) {
                        data.append(`team_events[${index}][event_name]`, item?.event_name)
                    }
                    if (item?.number_of_participants) {
                        data.append(`team_events[${index}][number_of_participants]`, item?.number_of_participants)
                    }
                    if (item?.price) {
                        data.append(`team_events[${index}][price]`, item?.price)
                    }
                    if (item?.event_details?.length > 0) {
                        item?.event_details?.forEach((eventDetailsItem, eventDetailsIndex) => {
                            if (eventDetailsItem?.event_type) {
                                data.append(`team_events[${index}][event_details][${eventDetailsIndex}][event_type]`, eventDetailsItem?.event_type)
                            }
                            if (eventDetailsItem?.age_groups?.length > 0) {
                                eventDetailsItem?.age_groups?.forEach((ageGroupsItem, ageGroupsIndex) => {
                                    if (ageGroupsItem?.age_group) {
                                        data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][age_group]`, ageGroupsItem?.age_group)
                                    }
                                    if (isAgeValueRequired(ageGroupsItem?.age_group) && ageGroupsItem?.age_value) {
                                        data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][age_value]`, ageGroupsItem?.age_value)
                                    }
                                    const genderCount = getGenderCount(eventDetailsItem?.event_type);
                                    if (genderCount) {
                                        data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][number_of_males]`, genderCount?.number_of_males)
                                        data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][number_of_females]`, genderCount?.number_of_females)
                                    }
                                    if (ageGroupsItem?.number_of_males && ageGroupsItem?.number_of_females && isMixedEvent(eventDetailsItem?.event_type) && (parseInt(ageGroupsItem?.number_of_males) + parseInt(ageGroupsItem?.number_of_females)) !== getPlayerCount(eventDetailsItem?.event_type)) {
                                        ErrorAlert(`You need to choose ${getPlayerCount(eventDetailsItem?.event_type)} person for ${convertEventType(eventDetailsItem?.event_type)} event`)
                                        return
                                    }
                                    if (ageGroupsItem?.number_of_males && isMixedEvent(eventDetailsItem?.event_type)) {
                                        data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][number_of_males]`, ageGroupsItem?.number_of_males)
                                    }
                                    if (ageGroupsItem?.number_of_females && isMixedEvent(eventDetailsItem?.event_type)) {
                                        data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][number_of_females]`, ageGroupsItem?.number_of_females)
                                    }
                                    data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][is_disabled]`, ageGroupsItem?.is_disabled ? 0 : 1)
                                    data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][allow_proffesional_players]`, ageGroupsItem?.allow_proffesional_players ? 1 : 0)
                                })
                            }
                        })
                    }
                })
            }
            data.append('contact', formdata?.contact)
            data.append('phone_number', formdata?.phone_number)
            data.append('address', formdata?.address)
            if (formdata?.category_type?.includes("TEAM")) {
                data.append('number_of_team_event', parseInt(formdata?.number_of_team_event))
            }
            setLoading(true)
            const result = await updateTournamentService({
                id: tournamentId,
                data
            });
            if (result?.data?.status) {
                SuccessAlert(result?.data?.message)
                props?.fetchOrganizerSingleTournament()
                handleClose()
            } else {
                ErrorAlert(result?.response?.data?.message)
            }
            setLoading(false)
        } catch (error) {
            setLoading(false)
            ErrorAlert(error)
        }
    }

    /**
        * @function handleClose
        * @params
        * @description used to close edit tournament modal
    */
    const handleClose = async () => {
        reset()
        setTournamentImage()
        props.handleClose()
    }

    return <Modal show={props.show} onHide={handleClose} size={props.size} centered backdrop="static" keyboard={false}>
        <LoadingOverlay
            active={loading}
            spinner={<Spinner animation="border" />}
        >
            <Modal.Header className='fs-22 fw-bold border-0' closeButton >
                Edit Tournament
            </Modal.Header>
            <Modal.Body className='d-flex flex-column align-items-center'>
                <Form className='w-100' onSubmit={handleSubmit(onSubmit)}>
                    <input className='d-none' type='file' id="tournamentPhotoUpload" accept="image/*" onChange={(e) => {
                        if (e.target.files.length > 0) {
                            const file = e.target.files[0];
                            const img = document.createElement("img");
                            img.onload = function () {
                                if (img.width <= 960 && (img.width / img.height)?.toFixed(2) === (23 / 9)?.toFixed(2)) {
                                    setTournamentImage(file)
                                } else {
                                    ErrorAlert("Please upload the correct ratio tournament image. Ratio will be 23/9")
                                }
                            };
                            img.src = URL.createObjectURL(file);
                        }
                    }} onClick={(e) => e.target.value = null} />
                    <label className='w-100' htmlFor="tournamentPhotoUpload">
                        {tournamentImage instanceof File
                            ?
                            <img role='button' className='w-100 img-fluid' src={URL.createObjectURL(tournamentImage)} alt={'tournamentImage'} />
                            :
                            tournamentImage ?
                                <img role='button' className='w-100 img-fluid' src={tournamentImage} alt={'tournamentImage'} />
                                :
                                <div role='button' className='d-flex flex-column justify-content-center align-items-center py-5'>
                                    <Image className='mb-3' />
                                    <span className='fs-18 fw-600 text-primary mb-3'>Add Banners here</span>
                                    <span className='fw-400 text-secondary'>Maximum width 960 pixels, 23:9 recommended</span>
                                </div>
                        }
                    </label>

                    <TournamentForm
                        {...{ register, control, watch, setValue, errors, individualEventsFields, individualEventsAppend, individualEventsRemove, teamEventsFields, teamEventsAppend }}
                    />

                    <div className='d-flex justify-content-end align-items-center mt-4'>
                        <span role='button' className='text-danger fw-bold px-3 me-2' onClick={() => {
                            Swal.fire({
                                title: "Are you sure ?",
                                text: "You won't be able to revert this!",
                                icon: "warning",
                                showCancelButton: true,
                                showCloseButton: true,
                                confirmButtonColor: "#FA5000",
                                cancelButtonColor: "#d33",
                                confirmButtonText: "Yes, Delete It!",
                                reverseButtons: true,
                                customClass: {
                                    container: 'delete-swal-container',
                                    title: 'fs-20 mb-3'
                                }
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    playersCountTournament()
                                }
                            });
                        }}>Delete Tournament</span>
                        <div>
                            <PrimaryButton className='fs-14 fw-500 px-5' disabled={loading} loading={loading} type="submit">Save</PrimaryButton>
                        </div>
                    </div>
                </Form>
            </Modal.Body>
        </LoadingOverlay>
    </Modal>
};

export default EditTournamentModal;